import type { AppEnumType } from "@shared/database/enums";
import { WORKER_ROUTE_MAP } from "@worker-contract/worker-messages";

const SANDBOX_APPS = {
  AGENT_AI: "agent-ai",
  MARKET_RESEARCH: "market-research",
} as const satisfies Partial<Record<AppEnumType, string>>;

export const isSandboxApp = (app: unknown): boolean =>
  typeof app === "string" && Object.hasOwn(SANDBOX_APPS, app);

/** Matches sandbox UI/API and provisioning-worker routes, never links in a query or fragment. */
export const isSandboxUrl = (value: unknown): boolean =>
  matchesSandboxRoute(value, false);

/** Drop the whole envelope item rather than attempting to recognise secrets in arbitrary content. */
export const filterSandboxTelemetry = <T>(event: T): T | null =>
  isSandboxTelemetry(event) ? null : event;

/** Uses SDK scope fields, not DOM links, arbitrary content or page-prefetch spans. */
const isSandboxTelemetry = (value: unknown): boolean => {
  const event = getTelemetryRecord(value);
  const data = getTelemetryRecord(event.data);
  return (
    isSandboxUrl(getTelemetryRecord(event.request).url) ||
    isSandboxUrl(event.transaction) ||
    isSandboxApp(getTelemetryRecord(event.extra).app) ||
    isSandboxApp(getTelemetryRecord(event.tags).app) ||
    hasSandboxApiUrl(event) ||
    (event.category === "navigation" &&
      (isSandboxUrl(data.from) || isSandboxUrl(data.to))) ||
    (Array.isArray(event.spans) && event.spans.some(hasSandboxApiUrl)) ||
    (Array.isArray(event.breadcrumbs) &&
      event.breadcrumbs.some(isSandboxTelemetry)) ||
    // rrweb metadata describes the current page; snapshots' nested hrefs do not.
    (event.type === 4 && isSandboxUrl(data.href)) ||
    (data.tag === "breadcrumb" && isSandboxTelemetry(data.payload)) ||
    (data.tag === "performanceSpan" && hasSandboxApiUrl(data.payload))
  );
};

const matchesSandboxRoute = (value: unknown, apiOnly: boolean): boolean => {
  if (typeof value !== "string") return false;
  // Transaction names and HTTP span descriptions may include the request method.
  const url = value.replace(/^[A-Z]+ /, "");
  if (!url.startsWith("/") && !/^https?:\/\//.test(url)) return false;
  let pathname: string;
  try {
    pathname = new URL(url, "https://telemetry.invalid").pathname;
  } catch {
    return false;
  }
  // Provisioning executes sandbox work outside the public application's route tree.
  if (
    pathname === WORKER_ROUTE_MAP.AGENT_AI_INSTANCE_PROVISION ||
    pathname === `${WORKER_ROUTE_MAP.AGENT_AI_INSTANCE_PROVISION}/`
  ) {
    return true;
  }
  return Object.values(SANDBOX_APPS).some((slug) => {
    const prefixes = apiOnly
      ? [`/${slug}/api`, `/api/${slug}`]
      : [`/${slug}`, `/api/${slug}`];
    return prefixes.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
  });
};

const hasSandboxApiUrl = (value: unknown): boolean => {
  const item = getTelemetryRecord(value);
  const data = getTelemetryRecord(item.data);
  return [item.description, data.url, data["http.url"], data["url.full"]].some(
    (url) => matchesSandboxRoute(url, true),
  );
};

// Replay, breadcrumb and event hooks have different SDK payload shapes.
const getTelemetryRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
